<template>
  <modal-component
    :is-displayed="isCreateModalOpened"
    :no-padding="true"
    :title="$t('BusControllers.CreateTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <label
              class="whitespace-nowrap text-sm font-medium not-italic text-gray-800"
              >{{ $t("BusControllers.ParkingSpace") }}</label
            >
            <div class="flex items-center justify-center">
              <small-loader
                v-if="isParkingSpaceListLoading"
                height="h-8"
                width="w-8"
              />
              <v-select
                v-else
                v-model="busController.spaceId"
                :class="`mt-1 w-full`"
                :options="parkingSpaces"
                :reduce="(space : any) => space.id"
                label="name"
              ></v-select>
            </div>
          </div>

          <input-field
            v-model="busController.imei"
            :error-message="v$?.busController['imei']?.$errors[0]?.$message"
            :is-error="v$?.busController['imei']?.$error"
            :label="$t('BusControllers.IMEI')"
            :required="true"
          />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import { CreateBusController } from "@/modules/Parkom/Submodules/BusControllers/Services/BusControllerTypes";
import { useToast } from "vue-toastification";
import BusControllerService from "@/modules/Parkom/Submodules/BusControllers/Services/BusControllerService";
import {
  ErrorMessageFormatter,
  isString,
} from "@/core/Services/GlobalFunctions";
import {
  ParkingSpace,
  ParkingSpaceObject,
} from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceTypes";
import ParkingSpaceService from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceService";
import useValidate from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";

export default defineComponent({
  components: {
    ButtonWrapper,
    VSelect,
    SmallLoader,
    FormDivider,
    InputField,
    FormWrapper,
    ModalComponent,
  },
  props: {
    isCreateModalOpened: Boolean,
    refreshData: {
      type: Function,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      busControllerService: new BusControllerService(),
      parkingSpaceService: new ParkingSpaceService(),
      parkingSpaces: [] as ParkingSpace[],
      toast: useToast(),
      v$: useValidate(),
      isParkingSpaceListLoading: false,
      isSubmitLoading: false,
      busController: {
        imei: "",
        spaceId: null,
      } as CreateBusController,
    };
  },
  validations() {
    return {
      busController: {
        imei: {
          required,
          minLength: minLength(15),
          maxLength: maxLength(15),
        },
      },
    };
  },
  async created() {
    try {
      await this.getParkingSpaces();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    }
  },
  methods: {
    async handleSubmit() {
      this.isSubmitLoading = true;
      try {
        await this.v$.$validate();
        if (this.v$.$error) {
          return;
        }
        await this.busControllerService.createBusController(this.busController);
        this.$emit("toggle-modal");
        this.toast.success("Bus controller created successfully!");
        await this.refreshData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    async getParkingSpaces() {
      this.isParkingSpaceListLoading = true;
      try {
        this.parkingSpaces = await this.parkingSpaceService.getParkingSpaces({
          pageNumber: 1,
          pageSize: 100000,
        });
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error("An unexpected error occurred.");
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isParkingSpaceListLoading = false;
      }
    },
  },
});
</script>

<style scoped></style>
